import {
  Edit,
  useNotify,
  useRedirect,
  useParams,
} from "react-admin";
import { apiUrl } from "../apiendpoint";

import {
  Box,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
} from "@mui/material";

import { useState, useEffect } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";



const AssessmentEdit = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const { id } = useParams();

  const [tab, setTab] = useState(0);

  // assessment fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState("");
  const [passingScore, setPassingScore] = useState("");

  // filters
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [difficulty, setDifficulty] = useState("");

  // data
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  // instructions
  const [instructions, setInstructions] = useState("");

  // total score
  const totalScore = selectedQuestions.reduce(
    (sum, q) => sum + (q.marks || 0),
    0
  );

  // ✅ LOAD EXISTING DATA
  const fetchAssessment = async () => {
    const res = await fetch(`${apiUrl}/assessments/${id}`);
    const data = await res.json();

    setTitle(data.title);
    setDescription(data.description);
    setDuration(data.duration);
    setPassingScore(data.passing_score);
    setInstructions(data.instructions || "");

    // ✅ IMPORTANT: map questions + marks
    const formattedQuestions = (data.questions || []).map((q) => ({
      ...q,
      marks: q.question_data?.marks || 0,
    }));

    setSelectedQuestions(formattedQuestions);
  };

  // ✅ FETCH QUESTIONS (FILTER)
  const fetchQuestions = async () => {
    const query = {
      filter: JSON.stringify({
        category_name: category || undefined,
        subcategory_name: subcategory || undefined,
        difficulty_level: difficulty || undefined,
      }),
      range: JSON.stringify([0, 50]),
    };

    const res = await fetch(
      `${apiUrl}/questions?${new URLSearchParams(query)}`
    );

    const data = await res.json();
    setQuestions(data);
  };

  useEffect(() => {
    fetchAssessment();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [category, subcategory, difficulty]);

  // ✅ SELECT QUESTION
  const toggleQuestion = (q) => {
    const exists = selectedQuestions.find((item) => item.id === q.id);

    if (exists) {
      setSelectedQuestions(selectedQuestions.filter((i) => i.id !== q.id));
    } else {
      setSelectedQuestions([...selectedQuestions, q]);
    }
  };

  // ✅ UPDATE
  const handleUpdate = async () => {
    const payload = {
      title,
      description,
      duration,
      passing_score: passingScore,
      total_score: totalScore,
      instructions,
      question_bank_ids: selectedQuestions.map((q) => q.id),
    };

    try {
      await fetch(`${apiUrl}/assessments/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      notify("Assessment updated 🎉", { type: "success" });
      redirect("/assessments");
    } catch (err) {
      notify("Error updating assessment", { type: "error" });
    }
  };

  return (
    <Edit title="Edit Assessment">
      <Box p={2}>
        {/* TABS */}
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label="Assessment" />
          <Tab label="Select Questions" />
          <Tab label="Instructions" />
        </Tabs>

        {/* TAB 1 */}
        {tab === 0 && (
          <Box mt={2}>
            <TextField
              label="Title"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              margin="normal"
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              margin="normal"
            />

            <TextField
              label="Duration"
              fullWidth
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              margin="normal"
            />

            <TextField
              label="Passing Score"
              fullWidth
              value={passingScore}
              onChange={(e) => setPassingScore(e.target.value)}
              margin="normal"
            />

            <Typography mt={2} fontWeight="bold">
              Total Score: {totalScore}
            </Typography>
          </Box>
        )}

        {/* TAB 2 */}
        {tab === 1 && (
          <Box mt={2}>
            {/* FILTERS */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={4}>
                <TextField
                  label="Category"
                  fullWidth
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  label="Subcategory"
                  fullWidth
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  label="Difficulty"
                  select
                  fullWidth
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="easy">Easy</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="hard">Hard</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {/* QUESTIONS */}
            {questions.map((q) => (
              <Paper
                key={q.id}
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    dangerouslySetInnerHTML={{ __html: q.title }}
                  />
                  <Typography>Marks: {q.marks}</Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedQuestions.some(
                        (item) => item.id === q.id
                      )}
                      onChange={() => toggleQuestion(q)}
                    />
                  }
                  label="Select"
                />
              </Paper>
            ))}

            <Typography mt={2} fontWeight="bold">
              Total Score: {totalScore}
            </Typography>
          </Box>
        )}

        {/* TAB 3 */}
        {tab === 2 && (
          <Box mt={2}>
            <Typography mb={1}>Instructions</Typography>

            <CKEditor
              editor={ClassicEditor}
              data={instructions}
              onChange={(event, editor) => {
                setInstructions(editor.getData());
              }}
            />
          </Box>
        )}

        {/* UPDATE BUTTON */}
        <Box mt={3}>
          <button onClick={handleUpdate}>Update Assessment</button>
        </Box>
      </Box>
    </Edit>
  );
};

export default AssessmentEdit;